import React, { use, useEffect } from 'react';
import SiteHubChart from './site-hub-chart';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { useTranslation } from '@hooks/index';
import SiteHubApi from '@apis/bff-api/central-monitoring/site-hub';

const ChartsView = () => {
    const { t }: { t: (key: string) => string } = useTranslation('cm');
    const { current } = use(StudySiteHubContext);
    const {
        sessionId: siteFilterSessionId,
        filtersChanged$,
        isRecalculating,
        isFiltered,
    } = useStudyDataFilterContext();

    const { data, isMutating, error, trigger } = SiteHubApi.useGetSitePanelsScoreData(
        current.studyRef,
        current.dataset.id,
        current.datasetFilters.dataSetsCount ?? 0,
        current.datasetFilters.dataSetFilter ?? 0,
        current.siteId,
    );

    useEffect(() => {
        const triggerRequest = () => {
            if (!current.siteId) {
                return;
            }
            trigger().catch((err) => window.console.log(err));
        };
        !(isRecalculating && isFiltered) && triggerRequest();

        // Subscribe to the filter changed event emitted by the Filter UI, and re-trigger the get
        const subscription = filtersChanged$.subscribe(() => {
            triggerRequest();
        });

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [
        current.siteId,
        current.studyRef,
        current.dataset.id,
        current.datasetFilters.dataSetsCount,
        current.datasetFilters.dataSetFilter,
        siteFilterSessionId,
        isRecalculating,
    ]);

    let content;

    if (isMutating || error || !current.siteId) {
        content = error ? <ErrorMessage /> : <Loading />;
        return (
            <Card title={t('studyHubPage.charts.panels')} data-testid="panels-section">
                {content}
            </Card>
        );
    }

    if (data?.length === 0) {
        return <InfoField message={t('siteHub.sparkLine.zeroPanelsConfigured')} type="Info" layout="center" />;
    }

    return (
        <div className="chart-view">
            {(data ?? []).map((panel) => (
                <SiteHubChart
                    siteId={current.siteId}
                    key={panel.reference}
                    panel={panel}
                    hidePanelSetup={current.hidePanelSetup}
                />
            ))}
        </div>
    );
};

export default ChartsView;
